// src/components/ResultsView.jsx
// Head marker "Results" tab: one row per student in a cohort, one column per
// homework, showing each student's score, late submissions and missed
// deadlines at a glance, with a CSV download. Read-only - it never writes.
// Used as the body of the Cohorts tab: onStudentClick opens a student's page,
// renderHeader shows the cohort's details, actions adds toolbar buttons.
import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { Download, Clock, ChevronDown } from 'lucide-react';
import { db } from '../firebase/config';
import useIsMobile from '../hooks/useIsMobile';
import { getAllCohorts, getCohortStudents, resolveEnabledHomeworkTypes } from '../firebase/cohorts';
import { getCohortSubmissions } from '../firebase/submissions';
import { getHomeworkNames, getMaxMarks, sortHomeworkTypes } from '../firebase/settings';

const RED = '#c33329';
const GREY = '#95a5a6';

// Statuses where the student has handed something in that isn't marked yet
const AWAITING_MARKING = ['pending', 'assigned', 'marking', 'flagged_for_review'];

const toDate = (value) => (value?.toDate ? value.toDate() : value ? new Date(value) : null);

const formatScore = (score) => {
  const total = score?.total;
  if (typeof total !== 'number') return null;
  return String(Math.round(total * 10) / 10);
};

const formatDue = (date) => date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const getCohortDeadlines = async (cohortId) => {
  try {
    const snap = await getDoc(doc(db, 'cohorts', cohortId, 'deadlines', 'current'));
    return snap.exists() ? snap.data() : {};
  } catch (error) {
    console.error('Get cohort deadlines error:', error);
    return {};
  }
};

// Which cohort to open on: the one with the next upcoming deadline (the one
// actively doing homework), else the one whose last deadline was most
// recent, else the newest cohort
const pickCurrentCohortId = (cohorts, deadlinesByCohort, now) => {
  let best = null;
  cohorts.forEach(cohort => {
    const deadlines = deadlinesByCohort[cohort.id] || {};
    const dates = Object.entries(deadlines)
      .filter(([key]) => !key.includes('_'))
      .map(([, value]) => toDate(value))
      .filter(d => d && !isNaN(d));
    const upcoming = dates.filter(d => d > now).sort((a, b) => a - b)[0];
    const lastPast = dates.filter(d => d <= now).sort((a, b) => b - a)[0];
    const candidate = upcoming
      ? { id: cohort.id, rank: 0, time: upcoming.getTime() }
      : lastPast ? { id: cohort.id, rank: 1, time: -lastPast.getTime() } : null;
    if (candidate && (!best || candidate.rank < best.rank || (candidate.rank === best.rank && candidate.time < best.time))) {
      best = candidate;
    }
  });
  return best ? best.id : cohorts[0]?.id || null;
};

// What one student has for one homework: their most recent submission that
// still counts (sent-back and student-deleted ones are ignored)
const describeCell = (submission, deadline, now) => {
  if (submission) {
    const late = !!submission.isLate;
    const score = formatScore(submission.score);
    if (submission.status === 'marked' && score !== null) return { kind: 'marked', score, late };
    if (submission.status === 'awaiting_review' && score !== null) return { kind: 'review', score, late };
    if (submission.status === 'marked' || submission.status === 'awaiting_review' || AWAITING_MARKING.includes(submission.status)) {
      return { kind: 'awaiting', late };
    }
  }
  if (deadline && deadline <= now) return { kind: 'missed' };
  return { kind: 'blank' };
};

const csvText = (cell) => {
  switch (cell.kind) {
    case 'marked': return cell.late ? `${cell.score} (late)` : cell.score;
    case 'review': return `${cell.score} (awaiting review)${cell.late ? ' (late)' : ''}`;
    case 'awaiting': return `Awaiting marking${cell.late ? ' (late)' : ''}`;
    case 'missed': return 'Not submitted';
    case 'off': return 'Not set';
    default: return '';
  }
};

const csvCell = (value) => {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const downloadCsv = (filename, rows) => {
  const blob = new Blob([rows.map(r => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.replace(/[^\w.-]+/g, '_');
  a.click();
  URL.revokeObjectURL(url);
};

// Red ring around a late submission's number or clock
const LateRing = ({ late, children }) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '30px',
    height: '30px',
    padding: '0 4px',
    boxSizing: 'border-box',
    borderRadius: '15px',
    border: late ? `2px solid ${RED}` : '2px solid transparent'
  }}>
    {children}
  </span>
);

const MissedDot = () => (
  <span style={{ display: 'inline-block', width: '14px', height: '14px', borderRadius: '50%', background: RED }} />
);

const ResultCell = ({ cell }) => {
  switch (cell.kind) {
    case 'marked':
      return <LateRing late={cell.late}><span style={{ fontWeight: '600', color: '#2c3e50' }}>{cell.score}</span></LateRing>;
    case 'review':
      return <LateRing late={cell.late}><span style={{ fontWeight: '600', color: GREY, fontStyle: 'italic' }}>{cell.score}</span></LateRing>;
    case 'awaiting':
      return <LateRing late={cell.late}><Clock size={16} color={GREY} /></LateRing>;
    case 'missed':
      return <MissedDot />;
    case 'off':
      return <span style={{ color: '#cbd5e0' }}>-</span>;
    default:
      return null;
  }
};

const KeyItem = ({ symbol, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#4a5568' }}>
    <span style={{ display: 'inline-flex', justifyContent: 'center', minWidth: '34px' }}>{symbol}</span>
    {label}
  </div>
);

function ResultsView({ orgId, initialCohortId, onCohortChange, onStudentClick, renderHeader, actions }) {
  const isMobile = useIsMobile();
  const [cohorts, setCohorts] = useState([]);
  const [deadlinesByCohort, setDeadlinesByCohort] = useState({});
  const [homeworkNames, setHomeworkNames] = useState({});
  const [maxMarks, setMaxMarks] = useState({});
  const [selectedCohortId, setSelectedCohortId] = useState(null);
  const [students, setStudents] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCohort, setLoadingCohort] = useState(false);
  const [error, setError] = useState('');
  const [showKey, setShowKey] = useState(false);

  const rememberKey = `resultsCohort:${orgId || 'default'}`;

  // Cohorts, their deadlines, and homework names/max marks - once
  useEffect(() => {
    (async () => {
      try {
        const [cohortList, names, marks] = await Promise.all([getAllCohorts(), getHomeworkNames(), getMaxMarks()]);
        const deadlinePairs = await Promise.all(cohortList.map(async c => [c.id, await getCohortDeadlines(c.id)]));
        const deadlines = Object.fromEntries(deadlinePairs);
        setCohorts(cohortList);
        setDeadlinesByCohort(deadlines);
        setHomeworkNames(names);
        setMaxMarks(marks);
        let remembered = null;
        try { remembered = localStorage.getItem(rememberKey); } catch (e) { remembered = null; }
        const startId = cohortList.some(c => c.id === initialCohortId)
          ? initialCohortId
          : cohortList.some(c => c.id === remembered)
            ? remembered
            : pickCurrentCohortId(cohortList, deadlines, new Date());
        setSelectedCohortId(startId);
      } catch (err) {
        console.error('Results load error:', err);
        setError('Could not load your cohorts. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    })();
  }, [rememberKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // The selected cohort's students and submissions
  useEffect(() => {
    if (!selectedCohortId) return;
    let cancelled = false;
    setLoadingCohort(true);
    setError('');
    (async () => {
      try {
        const [studentList, submissionList] = await Promise.all([
          getCohortStudents(selectedCohortId),
          getCohortSubmissions(selectedCohortId)
        ]);
        if (cancelled) return;
        setStudents(studentList);
        setSubmissions(submissionList);
      } catch (err) {
        console.error('Results cohort load error:', err);
        if (!cancelled) setError('Could not load this cohort\'s results. Please try again.');
      } finally {
        if (!cancelled) setLoadingCohort(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCohortId]);

  const handleSelectCohort = (cohortId) => {
    setSelectedCohortId(cohortId);
    try { localStorage.setItem(rememberKey, cohortId); } catch (e) { /* remembering is optional */ }
  };

  const cohort = cohorts.find(c => c.id === selectedCohortId) || null;

  useEffect(() => {
    if (cohort && onCohortChange) onCohortChange(cohort);
  }, [cohort]); // eslint-disable-line react-hooks/exhaustive-deps
  const deadlines = useMemo(() => deadlinesByCohort[selectedCohortId] || {}, [deadlinesByCohort, selectedCohortId]);

  const homeworkTypes = useMemo(() => {
    const allTypes = sortHomeworkTypes(Object.keys(homeworkNames));
    return resolveEnabledHomeworkTypes(cohort, allTypes);
  }, [homeworkNames, cohort]);

  const rows = useMemo(() => {
    const now = new Date();
    // Most recent counting submission per student + homework (the query
    // returns newest first)
    const latest = {};
    submissions.forEach(sub => {
      if (sub.deletedByStudent || sub.status === 'rejected') return;
      const key = `${sub.studentId}|${sub.homeworkType}`;
      if (!latest[key]) latest[key] = sub;
    });
    return students.map(student => {
      const enabledForStudent = resolveEnabledHomeworkTypes(cohort, homeworkTypes, student);
      const cells = homeworkTypes.map(type => {
        if (!enabledForStudent.includes(type)) return { kind: 'off' };
        return describeCell(latest[`${student.id}|${type}`], toDate(deadlines[type]), now);
      });
      return { student, cells };
    });
  }, [students, submissions, homeworkTypes, cohort, deadlines]);

  const handleDownload = () => {
    const header = ['Student', 'Email', ...homeworkTypes.map(t => `${t}${maxMarks[t] ? ` (/${maxMarks[t]})` : ''}`)];
    const body = rows.map(({ student, cells }) => [student.name || '', student.email || '', ...cells.map(csvText)]);
    const date = new Date().toISOString().slice(0, 10);
    downloadCsv(`Results ${cohort?.name || 'cohort'} ${date}.csv`, [header, ...body]);
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>Loading results...</div>;
  }

  return (
    <div style={{ background: 'white', borderRadius: '12px', padding: isMobile ? '14px' : '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        {renderHeader ? (
          <select
            value={selectedCohortId || ''}
            onChange={(e) => handleSelectCohort(e.target.value)}
            style={{ padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '15px', fontWeight: '600', minWidth: isMobile ? 0 : '220px', width: isMobile ? '100%' : 'auto' }}
            aria-label="Cohort"
          >
            {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}{c.active === false ? ' (inactive)' : ''}</option>)}
          </select>
        ) : (
        <div>
          <h2 style={{ margin: 0 }}>Results</h2>
          <div style={{ fontSize: '13px', color: '#7f8c8d', marginTop: '4px' }}>
            Each student's score for every homework in the cohort
          </div>
        </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', flexWrap: 'wrap' }}>
          {actions}
          {!renderHeader && (
          <select
            value={selectedCohortId || ''}
            onChange={(e) => handleSelectCohort(e.target.value)}
            style={{ padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', minWidth: '200px' }}
          >
            {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          )}
          <button
            onClick={handleDownload}
            disabled={loadingCohort || rows.length === 0}
            style={{
              padding: '10px 18px',
              background: RED,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: loadingCohort || rows.length === 0 ? 'not-allowed' : 'pointer',
              opacity: loadingCohort || rows.length === 0 ? 0.5 : 1,
              fontWeight: '600',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Download size={16} />
            Download CSV
          </button>
        </div>
      </div>

      {renderHeader && cohort && renderHeader(cohort, { studentCount: students.length })}

      {/* Key - collapsible, closed to start */}
      <div style={{ background: '#f8f9fa', borderRadius: '8px', marginBottom: '16px' }}>
        <div
          onClick={() => setShowKey(open => !open)}
          role="button"
          aria-expanded={showKey}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', cursor: 'pointer', userSelect: 'none' }}
        >
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>Key - what the symbols mean</span>
          <ChevronDown size={18} style={{ color: '#667eea', transform: showKey ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </div>
        {showKey && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px', padding: '0 16px 12px 16px' }}>
        <KeyItem symbol={<LateRing late={false}><span style={{ fontWeight: '600', color: '#2c3e50' }}>14</span></LateRing>} label="Marked - score" />
        <KeyItem symbol={<LateRing late><span style={{ fontWeight: '600', color: '#2c3e50' }}>14</span></LateRing>} label="Submitted late" />
        <KeyItem symbol={<LateRing late={false}><span style={{ fontWeight: '600', color: GREY, fontStyle: 'italic' }}>14</span></LateRing>} label="Marked, awaiting your review (not yet released)" />
        <KeyItem symbol={<LateRing late={false}><Clock size={16} color={GREY} /></LateRing>} label="Submitted, not yet marked" />
        <KeyItem symbol={<MissedDot />} label="Deadline passed, not submitted" />
        <KeyItem symbol={<span style={{ color: '#cbd5e0' }}>-</span>} label="Homework not set for this student" />
        <KeyItem symbol={<span style={{ display: 'inline-block', width: '14px' }} />} label="Blank - not due yet, or no deadline set" />
      </div>
        )}
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#fdecea', border: `1px solid ${RED}`, color: '#8a1f17', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>
          {error}
        </div>
      )}

      {loadingCohort ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>Loading cohort...</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>No students in this cohort yet.</div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '14px' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{ position: 'sticky', left: 0, background: '#f8f9fa', textAlign: 'left', padding: isMobile ? '8px 10px' : '10px 14px', borderBottom: '2px solid #e2e8f0', minWidth: isMobile ? '120px' : '180px', zIndex: 1 }}>
                  Student
                </th>
                {homeworkTypes.map(type => {
                  const due = toDate(deadlines[type]);
                  return (
                    <th key={type} title={homeworkNames[type] || type} style={{ padding: '10px 8px', borderBottom: '2px solid #e2e8f0', textAlign: 'center', whiteSpace: 'nowrap', minWidth: '64px' }}>
                      <div style={{ fontWeight: '700', color: '#2c3e50' }}>{type}</div>
                      <div style={{ fontSize: '11px', color: '#7f8c8d', fontWeight: '500' }}>
                        {maxMarks[type] ? `/${maxMarks[type]}` : ''}
                      </div>
                      <div style={{ fontSize: '11px', color: '#95a5a6', fontWeight: '500' }}>
                        {due ? `Due ${formatDue(due)}` : 'No deadline'}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ student, cells }) => (
                <tr key={student.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ position: 'sticky', left: 0, background: 'white', padding: isMobile ? '8px 10px' : '8px 14px', borderBottom: '1px solid #f0f0f0', zIndex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                      {onStudentClick ? (
                        <button
                          onClick={() => onStudentClick(student, cohort)}
                          title="Open this student's page"
                          style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: '600', color: '#2563eb', cursor: 'pointer', textAlign: 'left', textDecoration: 'underline', textUnderlineOffset: '3px' }}
                        >
                          {student.name || student.email}
                        </button>
                      ) : (student.name || student.email)}
                      {student.suspended && <span style={{ marginLeft: '6px', fontSize: '11px', color: RED, fontWeight: '600' }}>(suspended)</span>}
                    </div>
                    {!isMobile && <div style={{ fontSize: '12px', color: '#95a5a6' }}>{student.email}</div>}
                  </td>
                  {cells.map((cell, i) => (
                    <td key={homeworkTypes[i]} style={{ textAlign: 'center', padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                      <ResultCell cell={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ResultsView;
